import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Loader2, ChefHat, Check, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";

const FEATURES = [
  "5 AI-generated recipes every day",
  "Restaurant-grade Nano Banana plating photos",
  "Save unlimited recipes to your cookbook",
  "Share your cookbook with a proper OG cover link",
  "Every future feature, on the house",
];

const Subscribe = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [me, setMe] = useState(null);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    api.get("/subscription/plans").then((r) => setPlans(r.data?.plans || [])).catch(() => setPlans([]));
    if (user) {
      api.get("/subscription/me").then((r) => setMe(r.data)).catch(() => setMe(null));
    }
  }, [user]);

  const start = async (plan) => {
    if (!user) {
      toast.info("Sign in first — takes ten seconds.");
      navigate("/?auth=1");
      return;
    }
    setBusy(plan);
    try {
      const res = await api.post("/subscription/checkout", {
        plan,
        origin_url: window.location.origin,
      });
      window.location.href = res.data.checkout_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't start checkout");
      setBusy(null);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-950">
        <Loader2 className="w-6 h-6 animate-spin text-orange-600" />
      </div>
    );
  }

  const isActive = me?.is_active;

  return (
    <div className="min-h-screen bg-stone-950 pt-16 pb-32">
      <div className="max-w-[1200px] mx-auto px-6 sm:px-10 pt-16">
        <div className="mb-16 border-b border-stone-800 pb-12">
          <div className="flex items-center gap-4 mb-6">
            <ChefHat className="w-5 h-5 text-orange-500" />
            <span className="eyebrow text-orange-500">The Kitchen Pass</span>
          </div>
          <h1
            data-testid="subscribe-title"
            className="font-serif-display text-6xl md:text-7xl lg:text-8xl text-stone-100 leading-[0.9] tracking-tighter"
          >
            Every plate at <span className="italic text-orange-600">Cinder</span>
            <br />
            <span className="italic text-orange-600">&amp; Sorrel</span>, on tap.
          </h1>
          <p className="mt-8 max-w-2xl text-stone-300 leading-relaxed">
            A Kitchen Pass unlocks the whole <em className="text-stone-100 not-italic font-serif-display">Cinder &amp; Sorrel</em> kitchen &mdash;
            five bespoke recipes a day, plated by Nano Banana like the good bit of a Sunday supplement. Every AI recipe costs the
            kitchen a few pence in Claude and image calls; the pass covers the tab and keeps the lights on.
          </p>
        </div>

        {isActive && (
          <div className="mb-12 border border-lime-800/50 bg-lime-950/20 p-6 flex items-center gap-4 flex-wrap">
            <Check className="w-5 h-5 text-lime-500 shrink-0" />
            <p className="text-stone-200 flex-1 min-w-[240px]">
              You&apos;re already on the pass ({me.subscription_plan || "active"}).
              {me.subscription_current_period_end && (
                <>
                  {" "}
                  Renews {new Date(me.subscription_current_period_end).toLocaleDateString()}.
                </>
              )}
            </p>
            <Link
              to="/generate"
              data-testid="subscribe-to-generate"
              className="text-orange-500 hover:text-orange-400 ink-link text-sm"
            >
              Off to the pass →
            </Link>
            <Link
              to="/account"
              data-testid="subscribe-to-account"
              className="text-stone-400 hover:text-orange-500 ink-link text-sm"
            >
              Manage account →
            </Link>
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-6 mb-16" data-testid="subscribe-plans">
          {plans.map((p, i) => {
            const isAnnual = p.key === "annual";
            return (
              <div
                key={p.key}
                data-testid={`subscribe-plan-${p.key}`}
                className={`relative border ${isAnnual ? "border-orange-600" : "border-stone-800"} bg-stone-900/40 p-8 md:p-10`}
              >
                {isAnnual && (
                  <div className="absolute -top-3 left-8 bg-orange-600 text-stone-950 text-[10px] uppercase tracking-[0.28em] px-3 py-1">
                    Best value
                  </div>
                )}
                <span className="magazine-number text-3xl text-orange-500">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <h2 className="mt-4 font-serif-display text-4xl md:text-5xl text-stone-100 leading-none tracking-tight">
                  {p.label.replace("Kitchen Pass — ", "")}
                </h2>
                <div className="mt-4 flex items-baseline gap-2">
                  <span className="font-serif-display text-5xl sm:text-6xl text-stone-100">
                    £{p.amount.toFixed(0)}
                  </span>
                  <span className="text-sm text-stone-400">
                    /{p.interval === "year" ? "year" : "month"}
                  </span>
                </div>
                <p className="mt-3 font-serif-display italic text-stone-400">{p.tagline}</p>
                <button
                  type="button"
                  disabled={busy === p.key || isActive}
                  onClick={() => start(p.key)}
                  data-testid={`subscribe-start-${p.key}`}
                  className={`mt-8 w-full inline-flex items-center justify-center gap-3 ${
                    isAnnual ? "bg-orange-700 hover:bg-orange-800" : "bg-stone-800 hover:bg-stone-700"
                  } disabled:opacity-60 text-stone-50 px-6 py-4 uppercase text-xs tracking-[0.24em] font-medium transition-colors`}
                >
                  {busy === p.key ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Redirecting
                    </>
                  ) : isActive ? (
                    <>Already on the pass</>
                  ) : (
                    <>
                      Start {p.interval === "year" ? "annual" : "monthly"} pass
                      <ArrowUpRight className="w-4 h-4" />
                    </>
                  )}
                </button>
              </div>
            );
          })}
        </div>

        <div className="border border-stone-800 p-8">
          <h3 className="eyebrow mb-6">What&apos;s included</h3>
          <ul className="grid md:grid-cols-2 gap-4">
            {FEATURES.map((f) => (
              <li key={f} className="flex items-start gap-3 text-stone-200">
                <Check className="w-4 h-4 text-orange-500 mt-1 shrink-0" />
                <span>{f}</span>
              </li>
            ))}
          </ul>
          <p className="mt-8 text-xs uppercase tracking-[0.24em] text-stone-500">
            Cancel anytime · Test card 4242 4242 4242 4242 · any future expiry, any CVC
          </p>
        </div>
      </div>
    </div>
  );
};

export default Subscribe;
